import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const EVENT_OPTIONS = [
  { value: 'created', labelKey: 'common.events.created' },
  { value: 'activated', labelKey: 'common.events.activated' },
  { value: 'revoked', labelKey: 'common.events.revoked' },
  { value: 'updated', labelKey: 'common.events.updated' },
  { value: 'deactivated', labelKey: 'common.events.deactivated' },
] as const;

function useFilterParam(key: string): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? '';
  const set = useCallback(
    (next: string) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next.trim()) p.set(key, next.trim());
        else p.delete(key);
        p.set('page', '1');
        return p;
      });
    },
    [key, setParams]
  );
  return [value, set];
}

function useDebouncedParam(key: string, delay = 300): [string, (v: string) => void] {
  const [raw, setRaw] = useState('');
  const [param, setParam] = useFilterParam(key);
  useEffect(() => {
    setRaw(param);
  }, [param]);
  useEffect(() => {
    const t = setTimeout(() => setParam(raw), delay);
    return () => clearTimeout(t);
  }, [raw, setParam, delay]);
  return [raw, setRaw];
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-2.5 text-[13px] font-semibold text-muted-foreground">{children}</h3>;
}

export function AuditFilters() {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const [serial, setSerial] = useDebouncedParam('serial', 300);
  const [actor, setActor] = useDebouncedParam('actor', 300);
  const [from, setFrom] = useFilterParam('from');
  const [to, setTo] = useFilterParam('to');

  const events = params.get('event')?.split(',').filter(Boolean) ?? [];
  const toggleEvent = (value: string) => {
    const next = events.includes(value) ? events.filter((v) => v !== value) : [...events, value];
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next.length) p.set('event', next.join(','));
      else p.delete('event');
      p.set('page', '1');
      return p;
    });
  };

  const hasFilters =
    serial.trim().length > 0 ||
    actor.trim().length > 0 ||
    from.trim().length > 0 ||
    to.trim().length > 0 ||
    events.length > 0;

  const clearAll = () => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('event');
      p.delete('serial');
      p.delete('actor');
      p.delete('from');
      p.delete('to');
      p.set('page', '1');
      return p;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <SectionLabel>{t('audit.filters.event')}</SectionLabel>
        <div className="space-y-2">
          {EVENT_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <Checkbox
                id={`event-${opt.value}`}
                checked={events.includes(opt.value)}
                onCheckedChange={() => toggleEvent(opt.value)}
              />
              <Label htmlFor={`event-${opt.value}`} className="text-[13.5px] font-normal">
                {t(opt.labelKey)}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t pt-5">
        <SectionLabel>{t('audit.filters.date')}</SectionLabel>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="audit-from" className="text-[13px] font-normal text-faint">
              {t('audit.filters.fromDate')}
            </Label>
            <Input
              id="audit-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label={t('audit.filters.fromDate')}
              className="h-9 border-input bg-transparent text-[13px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-to" className="text-[13px] font-normal text-faint">
              {t('audit.filters.toDate')}
            </Label>
            <Input
              id="audit-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label={t('audit.filters.toDate')}
              className="h-9 border-input bg-transparent text-[13px]"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-5">
        <SectionLabel>{t('audit.filters.serial')}</SectionLabel>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
            aria-hidden="true"
          />
          <Input
            placeholder={t('audit.filters.searchSerial')}
            className="h-9 border-input bg-transparent pl-9 font-mono text-[13px]"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            aria-label={t('audit.filters.searchBySerialAria')}
          />
        </div>
      </div>

      <div className="border-t pt-5">
        <SectionLabel>{t('audit.filters.actor')}</SectionLabel>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
            aria-hidden="true"
          />
          <Input
            placeholder={t('audit.filters.searchActor')}
            className="h-9 border-input bg-transparent pl-9 text-[13px]"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            aria-label={t('audit.filters.searchByActorAria')}
          />
        </div>
      </div>

      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAll}
          className="gap-1 self-start text-[13px] text-muted-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
          {t('common.clearFilters')}
        </Button>
      )}
    </div>
  );
}
